// FILE: src/shared/lib/trust-utils.ts
// VERSION: 1.0.0
// START_MODULE_CONTRACT:
// PURPOSE: [Общие хелперы для отображения рейтинга ЧОП (Trust Score badge, ранжирование, возраст компании, композитный рейтинг v2)]
// SCOPE: [rating, reestr-chop, trust_score, ui, composite-rating]
// INPUT: [score: number, rank: number, registrationDate: string | null, chopId: string]
// OUTPUT: [{ color, label }, string, string, CompositeRating]
// KEYWORDS: [DOMAIN(SEO): trust-score; CONCEPT(badge): ранжирование, возраст, композитный рейтинг; UI: отображение; CACHE: in-memory, TTL]
// LINKS: [USES_IN(rating/page.tsx, rating/[region]/page.tsx, tools/reestr-chop/page.tsx); USES_API(calculateCompositeRating)]
// END_MODULE_CONTRACT



/**
 * Цвет и метка бейджа по Trust Score.
 * Пороги: >=30 — Высокий (зелёный), >=20 — Средний (оранжевый), <20 — Базовый (нейтральный).
 */
export function getTrustBadge(score: number): { color: string; label: string } {
  if (score >= 30)
    return { color: "text-green-400 border-green-400/30 bg-green-400/5", label: "Высокий" };
  if (score >= 20)
    return { color: "text-amber-600 border-orange-400/30 bg-orange-400/5", label: "Средний" };
  return { color: "text-white/40 border-white/10", label: "Базовый" };
}

/**
 * Акцент border для топ-3 позиций в рейтинге.
 * 1 — золото (orange-400), 2 — серебро (white/40), 3 — бронза (orange-700).
 */
export function getTopRankStyle(rank: number): string {
  if (rank === 1) return "border-l-2 border-l-orange-400";
  if (rank === 2) return "border-l-2 border-l-white/40";
  if (rank === 3) return "border-l-2 border-l-orange-700";
  return "";
}

/**
 * Возраст компании в годах на основе registration_date.
 * При отсутствии даты возвращает "н/д", для возраста <1 года — "<1 г.".
 */
export function getAge(registrationDate: string | null): string {
  if (!registrationDate) return "н/д";
  const years = Math.floor(
    (Date.now() - new Date(registrationDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000)
  );
  if (years < 1) return "<1 г.";
  return `${years} г.`;
}

/*
 * START_FUTURE: Composite Rating v2 (Trust Score)
 * Эти функции были добавлены на этапе проектирования, но пока не используются
 * в рантайме. Ни один компонент/роут не вызывает getCompositeRating,
 * invalidateCompositeRatingCache, clearAllCompositeRatingCache.
 *
 * План интеграции: вызвать getCompositeRating после успешной публикации отзыва
 * (в add-review-dialog.tsx или chop-review-form.tsx) для пересчёта trust_score
 * и обновления кэша.
 *
 * При раскомментировании потребуется:
 *   1. Раскомментировать импорты:
 *        import { calculateCompositeRating } from ".../composite-rating";
 *        import type { CompositeRating } from ".../composite-rating";
 *        import { createAdminClient } from ".../supabase/admin";
 *   2. Импорт serverLogger уже есть (строчка выше).
 * END_FUTURE
 */

/* START_FUTURE_BLOCK
import { calculateCompositeRating } from "@oxpa/shared/services/trust-score/composite-rating";
import type { CompositeRating } from "@oxpa/shared/services/trust-score/composite-rating";
import { createAdminClient } from "@oxpa/shared/lib/supabase/admin";

interface CacheEntry {
  data: CompositeRating;
  timestamp: number;
}

const ratingCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000;

export async function getCompositeRating(
  chopId: string,
): Promise<CompositeRating> {
  const cached = ratingCache.get(chopId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  const rating = await calculateCompositeRating(chopId);

  try {
    const supabase = createAdminClient();
    const trustScore = Math.round(rating.overall * 20);

    const { error: updateError } = await supabase
      .from("seo_companies")
      .update({ trust_score: trustScore })
      .eq("id", chopId);

    if (updateError) {
      serverLogger.error(
        "[TrustUtils][getCompositeRating] Failed to persist trust_score",
        { chopId, trustScore, error: updateError },
      );
    }
  } catch (err) {
    serverLogger.error(
      "[TrustUtils][getCompositeRating] Exception while persisting trust_score",
      { chopId, error: err },
    );
  }

  ratingCache.set(chopId, {
    data: rating,
    timestamp: Date.now(),
  });

  return rating;
}

export function invalidateCompositeRatingCache(chopId: string): void {
  ratingCache.delete(chopId);
}

export function clearAllCompositeRatingCache(): void {
  ratingCache.clear();
}
END_FUTURE_BLOCK */
